// The "this is loading" placeholder.
//
// ONE RULE, TWO SHAPES. The app had three idioms doing this job and they were
// picked by whoever wrote the screen:
//
//   Loader2 spinner        130 usages
//   hand-written pulse     ~25 divs, each retyping "animate-pulse rounded bg-secondary"
//   local Skeleton*        4 near-identical copies in Home, Pages, Sites, Insights
//
// The rule now is about WHAT IS HAPPENING, not who wrote the file:
//
//   SPINNER  — the user just did something and it is in flight. A button they
//              pressed, a mutation, a save. It belongs ON the control they
//              touched, and Loader2 inside <Button> stays exactly as it is.
//   SKELETON — content that is about to appear and whose shape is already
//              known. First loads, panels, tables, cards.
//
// The difference the user feels: a spinner says "wait", a skeleton says "this
// is what is coming". On a first load a spinner is the worse answer, because it
// throws away the layout information the app already has.
//
// WHY THE ROW COUNTS AND PADDING ARE PROPS. The three local copies had drifted
// to 4, 5 and 6 rows and to px-5 vs px-6 for no reason anyone could reconstruct.
// They are arguments now so the drift cannot happen silently again, and so a
// skeleton can be made to match the real list it stands in for.

import { cn } from "@/lib/utils";

/**
 * One placeholder block. Give it the size of the thing it replaces:
 * `<Skeleton className="h-3 w-1/3" />`.
 */
export function Skeleton({ className, inline = false }: { className?: string; inline?: boolean }) {
  const base = "animate-pulse rounded bg-secondary";
  // A placeholder standing in for a NUMBER or a word sits inside text flow, and
  // a <div> there is a block box in an inline context. One stat card was the
  // only hand-written pulse left after the sweep for exactly this reason.
  if (inline) return <span className={cn(base, "inline-block align-middle", className)} />;
  return <div className={cn(base, className)} />;
}

/**
 * The list-of-rows shape, which is what nearly every loading panel in this app
 * actually needs: a stack of rows, each with a two-line text block and
 * something on the right.
 *
 * Match `rows` to the page size the real list uses, so the layout does not jump
 * when the data lands.
 */
export function SkeletonRows({
  rows = 5,
  /** A square on the left, for lists that show a favicon or avatar. */
  avatar = false,
  /** Width of the block on the right. Null drops it entirely. */
  trailing = "w-20",
  className,
  rowClassName = "px-6 py-4",
}: {
  rows?: number;
  avatar?: boolean;
  trailing?: string | null;
  className?: string;
  rowClassName?: string;
}) {
  return (
    <div className={cn("divide-y", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className={cn("flex items-center gap-3", rowClassName)}>
          {avatar ? <Skeleton className="h-9 w-9 rounded-md" /> : null}
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          {trailing ? <Skeleton className={cn("h-7", trailing)} /> : null}
        </div>
      ))}
    </div>
  );
}

/**
 * A whole panel's worth, for a card or region that has no row structure to
 * imitate. Replaces the `<div className="h-48 animate-pulse …" />` blocks and,
 * more importantly, the panel-level spinners: those said "wait" where they
 * could have said "a card of about this size is coming".
 */
export function SkeletonPanel({ className }: { className?: string }) {
  return <Skeleton className={cn("h-48 w-full rounded-lg", className)} />;
}
